// Figma: "Loyalty Cloud" file, node 18551:3251 — CRM residents table "Filter" popover,
// opened from the Filter button in ./ResidentsTable.js. Each condition is
// {column, operator, value}; conditions are applied client-side over the rows already
// loaded by ResidentsTable (there's no per-column/operator filter param on the backend
// residents endpoint today — see getResidentsByAccountSfidV2 in residentsSlice.js, which
// only takes a single `search` string).
import React from 'react';
import PropTypes from 'prop-types';
import {ButtonBase, MenuItem, Popover, Select, Stack, TextField, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {tokens} from '../../../themes/ThemeCustomization';

const TEXT_OPERATORS = [
    {value: 'contains', label: 'contains'},
    {value: 'equals', label: 'equals'}
];

const NUMERIC_OPERATORS = [
    {value: 'equals', label: 'equals'},
    {value: 'greater_than', label: 'greater than'},
    {value: 'less_than', label: 'less than'}
];

// `type` decides which operator list a column offers; ResidentsTable's COLUMNS entries
// don't carry a type today, so it's declared here alongside the field/label pairing.
// `apiField` marks columns the backend's /v2/residents/filter/ endpoint accepts (see
// getFilteredResidents in residentsSlice.js) — only name/email/phone today. Conditions on
// those columns are sent to the API; every other column still filters client-side over the
// rows already loaded, same as before this endpoint existed.
export const FILTER_COLUMNS = [
    {field: 'name', label: 'Name', type: 'text', apiField: 'name'},
    {field: 'community', label: 'Community', type: 'text'},
    {field: 'campaign', label: 'Campaign', type: 'text'},
    {field: 'email', label: 'Email', type: 'text', apiField: 'email'},
    {field: 'phone', label: 'Phone', type: 'text', apiField: 'phone'},
    {field: 'unit', label: 'Unit', type: 'text'},
    {field: 'floorPlan', label: 'Floor plan', type: 'text'},
    {field: 'rent', label: 'Rent', type: 'number'},
    {field: 'balance', label: 'Ledger balance', type: 'number'},
    {field: 'leaseStart', label: 'Lease start date', type: 'date'},
    {field: 'leaseEnd', label: 'Lease end date', type: 'date'},
    {field: 'offers', label: 'Current offers', type: 'number'}
];

// Splits applied conditions into the subset the backend filter API can evaluate (name/
// email/phone) and the remainder that still needs applyFilters() over loaded rows.
export const splitFilterConditions = (conditions) => {
    const apiConditions = [];
    const clientConditions = [];
    (conditions || []).forEach((condition) => {
        const columnDef = FILTER_COLUMNS.find((c) => c.field === condition.column);
        if (columnDef?.apiField) {
            apiConditions.push({field: columnDef.apiField, operator: condition.operator, value: condition.value});
        } else {
            clientConditions.push(condition);
        }
    });
    return {apiConditions, clientConditions};
};

const operatorsForColumn = (field) => {
    const column = FILTER_COLUMNS.find((c) => c.field === field);
    return column?.type === 'number' || column?.type === 'date' ? NUMERIC_OPERATORS : TEXT_OPERATORS;
};

const makeCondition = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    column: FILTER_COLUMNS[0].field,
    operator: operatorsForColumn(FILTER_COLUMNS[0].field)[0].value,
    value: ''
});

const applyCondition = (row, {column, operator, value}) => {
    if (!value && value !== 0) return true;
    const columnDef = FILTER_COLUMNS.find((c) => c.field === column);
    const rowValue = row[column];

    if (columnDef?.type === 'number') {
        const numRow = Number(rowValue);
        const numValue = Number(value);
        if (!Number.isFinite(numRow) || !Number.isFinite(numValue)) return false;
        if (operator === 'greater_than') return numRow > numValue;
        if (operator === 'less_than') return numRow < numValue;
        return numRow === numValue;
    }

    if (columnDef?.type === 'date') {
        const dateRow = rowValue ? new Date(rowValue).getTime() : NaN;
        const dateValue = value ? new Date(value).getTime() : NaN;
        if (Number.isNaN(dateRow) || Number.isNaN(dateValue)) return false;
        if (operator === 'greater_than') return dateRow > dateValue;
        if (operator === 'less_than') return dateRow < dateValue;
        return dateRow === dateValue;
    }

    const textRow = String(rowValue ?? '').toLowerCase();
    const textValue = String(value).toLowerCase();
    return operator === 'equals' ? textRow === textValue : textRow.includes(textValue);
};

export const applyFilters = (rows, conditions) => {
    if (!conditions?.length) return rows;
    return rows.filter((row) => conditions.every((condition) => applyCondition(row, condition)));
};

const menuProps = {
    PaperProps: {
        sx: {
            mt: 0.5,
            borderRadius: '12px',
            border: `1px solid ${tokens.color.neutral200}`,
            boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px 0px rgba(0,0,0,0.06)',
            p: 1
        }
    },
    MenuListProps: {sx: {p: 0}}
};

const selectSx = {
    borderRadius: tokens.radius.control,
    fontSize: '14px',
    '& .MuiOutlinedInput-notchedOutline': {borderColor: tokens.color.neutral200},
    '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: tokens.color.neutral300},
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: tokens.color.sky600, borderWidth: '1px'}
};

const menuItemSx = {
    height: 36,
    px: 1,
    borderRadius: '6px',
    fontSize: '14px',
    color: tokens.color.neutral950,
    '&.Mui-selected': {backgroundColor: tokens.color.neutral200},
    '&.Mui-selected:hover': {backgroundColor: tokens.color.neutral200},
    '&:hover': {backgroundColor: tokens.color.neutral100}
};

const ConditionRow = ({condition, onChange, onRemove, showRemove}) => {
    const operators = operatorsForColumn(condition.column);

    const handleColumnChange = (field) => {
        const nextOperators = operatorsForColumn(field);
        onChange({...condition, column: field, operator: nextOperators[0].value});
    };

    return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{width: '100%'}}>
            {condition.isFirst && (
                <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral950, width: 56, flexShrink: 0}}>
                    Where
                </Typography>
            )}
            <Select
                size="small"
                value={condition.column}
                onChange={(e) => handleColumnChange(e.target.value)}
                sx={{...selectSx, flex: 1}}
                MenuProps={menuProps}
            >
                {FILTER_COLUMNS.map(({field, label}) => (
                    <MenuItem key={field} value={field} sx={menuItemSx}>
                        {label}
                    </MenuItem>
                ))}
            </Select>
            <Select
                size="small"
                value={condition.operator}
                onChange={(e) => onChange({...condition, operator: e.target.value})}
                sx={{...selectSx, flex: 1}}
                MenuProps={menuProps}
            >
                {operators.map(({value, label}) => (
                    <MenuItem key={value} value={value} sx={menuItemSx}>
                        {label}
                    </MenuItem>
                ))}
            </Select>
            <TextField
                size="small"
                placeholder="Enter value"
                value={condition.value}
                onChange={(e) => onChange({...condition, value: e.target.value})}
                sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: tokens.radius.control,
                        fontSize: '14px',
                        '& fieldset': {borderColor: tokens.color.neutral200},
                        '&:hover fieldset': {borderColor: tokens.color.neutral300},
                        '&.Mui-focused fieldset': {borderColor: tokens.color.sky600, borderWidth: '1px'}
                    }
                }}
            />
            <ButtonBase
                onClick={onRemove}
                disabled={!showRemove}
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    opacity: showRemove ? 1 : 0.3,
                    '&:hover': showRemove ? {backgroundColor: tokens.color.neutral100} : undefined
                }}
            >
                <DeleteOutlineIcon sx={{fontSize: 20, color: tokens.color.neutral600}} />
            </ButtonBase>
        </Stack>
    );
};

ConditionRow.propTypes = {
    condition: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    showRemove: PropTypes.bool
};

const FilterPopover = ({anchorEl, open, onClose, conditions, onApply}) => {
    const [draft, setDraft] = React.useState(conditions?.length ? conditions : [makeCondition()]);

    React.useEffect(() => {
        if (open) setDraft(conditions?.length ? conditions : [makeCondition()]);
    }, [open, conditions]);

    const updateCondition = (id, next) => {
        setDraft((prev) => prev.map((c) => (c.id === id ? next : c)));
    };

    const removeCondition = (id) => {
        setDraft((prev) => (prev.length > 1 ? prev.filter((c) => c.id !== id) : prev));
    };

    const addCondition = () => {
        setDraft((prev) => [...prev, makeCondition()]);
    };

    const handleClearAll = () => {
        setDraft([makeCondition()]);
        onApply([]);
        onClose();
    };

    const handleApply = () => {
        onApply(draft.filter((c) => c.value !== ''));
        onClose();
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
            PaperProps={{
                sx: {
                    width: '810px',
                    maxWidth: '90vw',
                    borderRadius: '12px',
                    border: `1px solid ${tokens.color.neutral200}`,
                    mt: 0.5
                }
            }}
        >
            <Stack spacing={2} sx={{p: 2}}>
                {draft.map((condition, index) => (
                    <ConditionRow
                        key={condition.id}
                        condition={{...condition, isFirst: index === 0}}
                        onChange={(next) => updateCondition(condition.id, next)}
                        onRemove={() => removeCondition(condition.id)}
                        showRemove={draft.length > 1}
                    />
                ))}
                <ButtonBase
                    onClick={addCondition}
                    sx={{display: 'flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start'}}
                >
                    <AddIcon sx={{fontSize: 18, color: tokens.color.sky600}} />
                    <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.sky600}}>
                        Add Condition
                    </Typography>
                </ButtonBase>
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1.5}
                sx={{p: 2, borderTop: `1px solid ${tokens.color.neutral200}`, backgroundColor: tokens.color.neutral50}}
            >
                <ButtonBase
                    onClick={handleClearAll}
                    sx={{
                        height: 32,
                        px: 2,
                        borderRadius: tokens.radius.control,
                        border: `1px solid ${tokens.color.neutral200}`,
                        backgroundColor: tokens.color.white
                    }}
                >
                    <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral950}}>
                        Clear all
                    </Typography>
                </ButtonBase>
                <ButtonBase
                    onClick={handleApply}
                    sx={{height: 32, px: 2, borderRadius: tokens.radius.control, backgroundColor: tokens.color.sky600}}
                >
                    <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.white}}>
                        Apply
                    </Typography>
                </ButtonBase>
            </Stack>
        </Popover>
    );
};

FilterPopover.propTypes = {
    anchorEl: PropTypes.any,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    conditions: PropTypes.array,
    onApply: PropTypes.func.isRequired
};

export default FilterPopover;
